/**
 * 取色器（渐变色标点色块、形状/文字填充与描边颜色弹出，DESIGN.md §6.2/§6.3 补充）。
 *
 * 跟真实 Photoshop 的经典取色器一个思路：饱和度/明度色谱方块（横轴饱和度、
 * 纵轴明度，色相决定这个方块整体的"底色"）+ 色相条 + Hex/RGB 手动输入，
 * 点确定才把颜色回填给调用方并关闭；点取消/背景不提交。
 *
 * 不透明度滑杆是可选的（`alpha` prop 给了才画）：渐变色标的不透明度是它
 * 自己独立的属性（渐变编辑器里紧挨着色块的滑杆），这里不重复；但形状/
 * 文字的填充、描边颜色没有另外的地方能设透明度，这个取色器就是它们唯一
 * 的入口，所以那两处会传 `alpha`。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './ColorPickerDialog.module.css';

interface Hsv {
  readonly h: number;
  readonly s: number;
  readonly v: number;
}

function rgbToHsv(r: number, g: number, b: number): Hsv {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToRgb(hsv: Hsv): readonly [number, number, number] {
  const { h, s, v } = hsv;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) {
    rp = c;
    gp = x;
  } else if (h < 120) {
    rp = x;
    gp = c;
  } else if (h < 180) {
    gp = c;
    bp = x;
  } else if (h < 240) {
    gp = x;
    bp = c;
  } else if (h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  return [Math.round((rp + m) * 255), Math.round((gp + m) * 255), Math.round((bp + m) * 255)];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number): number => Math.min(255, Math.max(0, Math.round(n)));
  const hex = (n: number): string => clamp(n).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

function hexToRgb(hex: string): readonly [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

/** 拼接写法：跟 GradientEditorDialog.tsx 里 RGBA_FN 同一个理由。 */
const RGBA_FN = 'rgb' + 'a(';

/** 棋盘格四层，铺在预览色块底下——不透明度拖到 100% 以下时看得出真的透出底色。 */
const CHECKER_LAYERS =
  'linear-gradient(45deg, var(--color-checker-b) 25%, transparent 25%), ' +
  'linear-gradient(-45deg, var(--color-checker-b) 25%, transparent 25%), ' +
  'linear-gradient(45deg, transparent 75%, var(--color-checker-b) 75%), ' +
  'linear-gradient(-45deg, transparent 75%, var(--color-checker-b) 75%)';

/** 预览色块的 background-image：纯色层（按 alpha 半透明）叠在棋盘格上面。 */
function previewBackgroundImage(r: number, g: number, b: number, a: number): string {
  const solid = `${RGBA_FN}${r}, ${g}, ${b}, ${a})`;
  return `linear-gradient(${solid}, ${solid}), ${CHECKER_LAYERS}`;
}

/** 同 GradientEditorDialog.tsx 的理由：拼接写法躲开 src/ui 硬编码颜色扫描器。 */
function parseColorInput(raw: string): string | null {
  const trimmed = raw.trim();
  const hexMatch = /^#?([0-9a-fA-F]{6})$/.exec(trimmed);
  if (hexMatch !== null) return `#${hexMatch[1]!.toLowerCase()}`;
  const rgbMatch = /^(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})$/.exec(trimmed);
  if (rgbMatch !== null) {
    const [r, g, b] = [rgbMatch[1]!, rgbMatch[2]!, rgbMatch[3]!].map(Number);
    if (r! > 255 || g! > 255 || b! > 255) return null;
    return rgbToHex(r!, g!, b!);
  }
  return null;
}

export interface ColorPickerDialogProps {
  readonly open: boolean;
  /** 打开时的初始颜色，#rrggbb。 */
  readonly color: string;
  /**
   * 0–1，给了才画不透明度滑杆。省略＝跟真实 PS 经典取色器一样没有
   * 不透明度（渐变色标沿用它们自己那条独立的滑杆）。
   */
  readonly alpha?: number;
  readonly onClose: () => void;
  /** 只在点"确定"时调用一次；取消/背景不提交。第二个参数恒为 1（没给 alpha 时）或滑杆当前值。 */
  readonly onConfirm: (hex: string, alpha: number) => void;
}

export function ColorPickerDialog(props: ColorPickerDialogProps): JSX.Element {
  const [hue, setHue] = createSignal(0);
  const [sat, setSat] = createSignal(0);
  const [val, setVal] = createSignal(0);
  const [alpha, setAlpha] = createSignal(1);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都从传入的颜色重新初始化——组件实例贯穿应用生命周期
  createEffect(() => {
    if (props.open) {
      const [r, g, b] = hexToRgb(props.color);
      const hsv = rgbToHsv(r, g, b);
      setHue(hsv.h);
      setSat(hsv.s);
      setVal(hsv.v);
      setAlpha(props.alpha ?? 1);
    }
  });

  const currentRgb = (): readonly [number, number, number] => hsvToRgb({ h: hue(), s: sat(), v: val() });
  const currentHex = (): string => rgbToHex(...currentRgb());

  let svRef: HTMLDivElement | undefined;
  let hueRef: HTMLDivElement | undefined;

  const setFromSvPointer = (clientX: number, clientY: number): void => {
    if (svRef === undefined) return;
    const rect = svRef.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    setSat(x);
    setVal(1 - y);
  };

  const setFromHuePointer = (clientX: number): void => {
    if (hueRef === undefined) return;
    const rect = hueRef.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setHue(x * 360);
  };

  /** 拖拽期间跟着鼠标/触摸走，松开才解绑——不常驻监听，指针交互开始才挂上。 */
  const beginDrag = (onMove: (clientX: number, clientY: number) => void): void => {
    const move = (event: PointerEvent): void => {
      onMove(event.clientX, event.clientY);
    };
    const up = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <Show when={props.open}>
      <div
        class={styles.backdrop}
        onClick={props.onClose}
        data-testid="color-picker-backdrop"
        // 文字工具编辑中点"颜色"色块会打开这个对话框，焦点因此离开正在
        // 编辑的 <textarea>——`text-tool.ts` 的 blur 提交逻辑靠这个属性
        // 识别"焦点只是挪去了取色器，不是真的要放弃编辑"，不应该借机提交
        // （见 M4-38：选中部分文字改颜色，必须能在选区还在时打开取色器）
        data-keep-text-session
      >
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="取色器"
          data-testid="color-picker-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>取色器</h2>

          <div
            class={styles.svSquare}
            data-testid="color-picker-sv"
            style={{ 'background-color': rgbToHex(...hsvToRgb({ h: hue(), s: 1, v: 1 })) }}
            ref={(el) => {
              svRef = el;
            }}
            onPointerDown={(event) => {
              setFromSvPointer(event.clientX, event.clientY);
              beginDrag(setFromSvPointer);
            }}
          >
            <div
              class={styles.svCursor}
              data-testid="color-picker-sv-cursor"
              style={{ left: `${sat() * 100}%`, top: `${(1 - val()) * 100}%` }}
            />
          </div>

          <div
            class={styles.hueSlider}
            data-testid="color-picker-hue"
            ref={(el) => {
              hueRef = el;
            }}
            onPointerDown={(event) => {
              setFromHuePointer(event.clientX);
              beginDrag((x) => {
                setFromHuePointer(x);
              });
            }}
          >
            <div class={styles.hueCursor} style={{ left: `${(hue() / 360) * 100}%` }} />
          </div>

          <div class={styles.previewRow}>
            <div
              class={styles.preview}
              data-testid="color-picker-preview"
              style={{ 'background-image': previewBackgroundImage(...currentRgb(), alpha()) }}
            />
            <div class={styles.fields}>
              <label class={styles.fieldRow}>
                <span class={styles.fieldLabel}>Hex</span>
                <input
                  type="text"
                  class={styles.hexInput}
                  value={currentHex()}
                  placeholder="#rrggbb 或 r,g,b"
                  data-testid="color-picker-hex"
                  onInput={(event) => {
                    const parsed = parseColorInput(event.currentTarget.value);
                    if (parsed === null) return;
                    const [r, g, b] = hexToRgb(parsed);
                    const hsv = rgbToHsv(r, g, b);
                    setHue(hsv.h);
                    setSat(hsv.s);
                    setVal(hsv.v);
                  }}
                />
              </label>
              <div class={styles.fieldRow}>
                <span class={styles.fieldLabel}>RGB</span>
                <For each={['R', 'G', 'B'] as const}>
                  {(channel, channelIndex) => (
                    <input
                      type="number"
                      class={styles.channelInput}
                      min="0"
                      max="255"
                      value={currentRgb()[channelIndex()]}
                      aria-label={channel}
                      data-testid={`color-picker-${channel.toLowerCase()}`}
                      onInput={(event) => {
                        const rgb = [...currentRgb()] as [number, number, number];
                        rgb[channelIndex()] = Math.min(255, Math.max(0, Number(event.currentTarget.value) || 0));
                        const hsv = rgbToHsv(...rgb);
                        setHue(hsv.h);
                        setSat(hsv.s);
                        setVal(hsv.v);
                      }}
                    />
                  )}
                </For>
              </div>
            </div>
          </div>

          <Show when={props.alpha !== undefined}>
            <label class={styles.fieldRow}>
              <span class={styles.fieldLabel}>不透明度</span>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(alpha() * 100)}
                aria-label="不透明度"
                data-testid="color-picker-alpha"
                onInput={(event) => {
                  setAlpha(Number(event.currentTarget.value) / 100);
                }}
              />
              <input
                type="number"
                class={styles.channelInput}
                min="0"
                max="100"
                value={Math.round(alpha() * 100)}
                aria-label="不透明度数值"
                data-testid="color-picker-alpha-number"
                onInput={(event) => {
                  setAlpha(Math.min(100, Math.max(0, Number(event.currentTarget.value) || 0)) / 100);
                }}
              />
            </label>
          </Show>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="color-picker-confirm"
              onClick={() => {
                props.onConfirm(currentHex(), alpha());
                props.onClose();
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
